import { ReactNode } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

export interface DropdownAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  destructive?: boolean;
}

interface DropdownProps {
  actions: DropdownAction[];
  align?: 'left' | 'right';
}

const Dropdown = ({ actions, align = 'right' }: DropdownProps) => (
  <Menu as="div" className="relative inline-block text-left">
    <MenuButton
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
      aria-label="Actions"
    >
      <EllipsisHorizontalIcon className="h-5 w-5" />
    </MenuButton>
    <MenuItems
      transition
      anchor={align === 'right' ? 'bottom end' : 'bottom start'}
      className={clsx(
        'z-50 mt-1 w-48 origin-top rounded-xl border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-800 dark:bg-gray-900',
        'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0'
      )}
    >
      {actions.map((action) => (
        <MenuItem key={action.label}>
          {({ focus }) => (
            <button
              type="button"
              onClick={action.onClick}
              className={clsx(
                'flex w-full items-center gap-2 px-4 py-2 text-left text-sm',
                action.destructive
                  ? 'text-error-500'
                  : 'text-gray-700 dark:text-gray-200',
                focus && (action.destructive ? 'bg-error-500/10' : 'bg-gray-100 dark:bg-gray-800')
              )}
            >
              {action.icon}
              {action.label}
            </button>
          )}
        </MenuItem>
      ))}
    </MenuItems>
  </Menu>
);

export default Dropdown;
